import * as React from "react";
import { useState, useEffect } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
    Button,
    CircularProgress,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormControl,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EventIcon from "@material-ui/icons/Event";
import SettingsIcon from "@material-ui/icons/Settings";
import ImageIcon from "@material-ui/icons/Image";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";

const ENDPOINT = API + "/referral-promo-banner-config";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: "#f5f5f5",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 600,
    },
    accordion: {
        marginBottom: theme.spacing(1),
        "&:before": {
            display: "none",
        },
        overflow: "hidden",
    },
    accordionSummary: {
        backgroundColor: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
    },
    sectionIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    sectionTitle: {
        fontWeight: 500,
    },
    fieldGrid: {
        display: "grid !important",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "32px 24px !important",
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: "1fr",
        },
    },
    textAreaGrid: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    submitButton: {
        marginTop: theme.spacing(3),
    },
    tableContainer: {
        marginBottom: theme.spacing(3),
    },
    statusChip: {
        fontWeight: 500,
    },
    activeChip: {
        backgroundColor: "#4caf50",
        color: "white",
    },
    inactiveChip: {
        backgroundColor: "#9e9e9e",
        color: "white",
    },
}));

// Form types
interface BannerFormData {
    name?: string;
    is_active?: boolean;
    priority?: string;
    start_date?: string;
    end_date?: string;
    target_type?: string;
    // Banner Config
    header_logo_url?: string;
    bg_color_1?: string;
    bg_color_2?: string;
    title?: string;
    subtitle?: string;
    middle_image_url?: string;
    middle_image_width?: string;
    middle_image_height?: string;
    // CTAs
    cta_primary_text?: string;
    cta_primary_action?: string;
    cta_primary_screen_name?: string;
    cta_primary_url?: string;
    cta_primary_bg_color?: string;
    cta_primary_text_color?: string;
    cta_secondary_text?: string;
    cta_secondary_action?: string;
    cta_secondary_screen_name?: string;
    cta_secondary_url?: string;
    // Footer
    footer_text?: string;
    footer_link_text?: string;
    footer_link_url?: string;
}

interface BannerRecord {
    id: number;
    name: string;
    is_active: boolean;
    priority: number;
    start_date: string | null;
    end_date: string | null;
    target_type: string;
    banner_config: any;
}

const { Form } = withTypes<BannerFormData>();

// Render helpers
const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    label,
    ...props
}: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <TextField
            {...props}
            {...inputProps}
            error={!!(touched && error)}
            helperText={touched && error}
            fullWidth
            variant="outlined"
            size="small"
        />
    </div>
);

const renderSwitch = ({ input, label }: any) => {
    const isChecked = input.checked !== undefined ? input.checked : !!input.value;
    return (
        <FormControlLabel
            control={
                <Switch
                    checked={isChecked}
                    onChange={(event) => {
                        input.onChange(event.target.checked);
                    }}
                    color="primary"
                />
            }
            label={label}
        />
    );
};

const renderSelect = ({ input, label, options, ...props }: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <FormControl fullWidth variant="outlined" size="small">
            <Select {...input} {...props}>
                {options.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    </div>
);

const ReferralPromoBannerConfig: React.FC = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(false);
    const [banners, setBanners] = useState<BannerRecord[]>([]);
    const [selectedBanner, setSelectedBanner] = useState<BannerRecord | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [expandedSections, setExpandedSections] = useState<string[]>(["basic"]);
    const [initialValues, setInitialValues] = useState<BannerFormData>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState<BannerRecord | null>(null);

    useEffect(() => {
        loadBanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBanners = async () => {
        setLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.get(ENDPOINT, config);
            const data = response.data?.data || response.data || [];
            setBanners(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load banners:", error);
            notify("Failed to load banners", { type: "error" });
        }
        setLoading(false);
    };

    const handleAccordionChange = (panel: string) => (_event: any, isExpanded: boolean) => {
        setExpandedSections(
            isExpanded
                ? [...expandedSections, panel]
                : expandedSections.filter((s) => s !== panel)
        );
    };

    const handleCreateNew = () => {
        setSelectedBanner(null);
        setIsCreating(true);
        setIsEditing(true);
        setInitialValues({
            is_active: false,
            priority: "0",
            target_type: "no_prize_draw_entry",
            bg_color_1: "#F5A623",
            bg_color_2: "#D4841C",
            cta_primary_action: "CLOSE",
            cta_primary_bg_color: "#1E3A5F",
            cta_primary_text_color: "#FFFFFF",
            cta_secondary_action: "CLOSE",
        });
        setExpandedSections(["basic", "content"]);
    };

    const handleEditBanner = (banner: BannerRecord) => {
        setSelectedBanner(banner);
        setIsCreating(false);
        setIsEditing(true);

        const config = banner.banner_config || {};

        setInitialValues({
            name: banner.name,
            is_active: banner.is_active,
            priority: String(banner.priority || 0),
            start_date: banner.start_date ? banner.start_date.substring(0, 16) : "",
            end_date: banner.end_date ? banner.end_date.substring(0, 16) : "",
            target_type: banner.target_type || "no_prize_draw_entry",
            // Banner config
            header_logo_url: config.header_logo_url || "",
            bg_color_1: config.background?.colors?.[0] || "#F5A623",
            bg_color_2: config.background?.colors?.[1] || "#D4841C",
            title: config.title || "",
            subtitle: config.subtitle || "",
            middle_image_url: config.middle_image_url || "",
            middle_image_width: config.middle_image_width ? String(config.middle_image_width) : "",
            middle_image_height: config.middle_image_height ? String(config.middle_image_height) : "",
            // CTAs
            cta_primary_text: config.cta_primary?.text || "",
            cta_primary_action: config.cta_primary?.action || "CLOSE",
            cta_primary_screen_name: config.cta_primary?.screen_name || "",
            cta_primary_url: config.cta_primary?.url || "",
            cta_primary_bg_color: config.cta_primary?.background_color || "#1E3A5F",
            cta_primary_text_color: config.cta_primary?.text_color || "#FFFFFF",
            cta_secondary_text: config.cta_secondary?.text || "",
            cta_secondary_action: config.cta_secondary?.action || "CLOSE",
            cta_secondary_screen_name: config.cta_secondary?.screen_name || "",
            cta_secondary_url: config.cta_secondary?.url || "",
            // Footer
            footer_text: config.footer?.text || "",
            footer_link_text: config.footer?.link_text || "",
            footer_link_url: config.footer?.link_url || "",
        });
        setExpandedSections(["basic"]);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsCreating(false);
        setSelectedBanner(null);
    };

    const handleDeleteClick = (banner: BannerRecord) => {
        setBannerToDelete(banner);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!bannerToDelete) return;

        try {
            const headers = getConfigPost();
            await axios.delete(`${ENDPOINT}/${bannerToDelete.id}`, { headers });
            notify("Banner deleted successfully", { type: "success" });
            setDeleteDialogOpen(false);
            setBannerToDelete(null);
            loadBanners();
        } catch (error) {
            console.error("Failed to delete banner:", error);
            notify("Failed to delete banner", { type: "error" });
        }
    };

    const handleSubmit = async (values: BannerFormData) => {
        setLoading(true);

        // Build banner_config JSON
        const bannerConfig = {
            header_logo_url: values.header_logo_url || null,
            background: {
                type: "gradient",
                colors: [values.bg_color_1 || "#F5A623", values.bg_color_2 || "#D4841C"],
                direction: "vertical",
            },
            title: values.title || "",
            subtitle: values.subtitle || "",
            middle_image_url: values.middle_image_url || null,
            middle_image_width: values.middle_image_width ? parseInt(values.middle_image_width) : null,
            middle_image_height: values.middle_image_height ? parseInt(values.middle_image_height) : null,
            cta_primary: {
                text: values.cta_primary_text || "",
                action: values.cta_primary_action || "CLOSE",
                screen_name: values.cta_primary_screen_name || null,
                url: values.cta_primary_url || null,
                background_color: values.cta_primary_bg_color || "#1E3A5F",
                text_color: values.cta_primary_text_color || "#FFFFFF",
            },
            cta_secondary: values.cta_secondary_text ? {
                text: values.cta_secondary_text || "",
                action: values.cta_secondary_action || "CLOSE",
                screen_name: values.cta_secondary_screen_name || null,
                url: values.cta_secondary_url || null,
            } : null,
            footer: values.footer_text || values.footer_link_text ? {
                text: values.footer_text || "",
                link_text: values.footer_link_text || "",
                link_url: values.footer_link_url || "",
            } : null,
        };

        const payload = {
            name: values.name,
            is_active: values.is_active || false,
            priority: parseInt(values.priority || "0"),
            start_date: values.start_date || null,
            end_date: values.end_date || null,
            target_type: values.target_type || "no_prize_draw_entry",
            banner_config: bannerConfig,
        };

        try {
            const headers = getConfigPost();

            if (isCreating) {
                await axios.post(ENDPOINT, payload, { headers });
                notify("Banner created successfully!", { type: "success" });
            } else if (selectedBanner) {
                await axios.patch(`${ENDPOINT}/${selectedBanner.id}`, payload, { headers });
                notify("Banner updated successfully!", { type: "success" });
            }

            setIsEditing(false);
            setIsCreating(false);
            setSelectedBanner(null);
            loadBanners();
        } catch (error: any) {
            console.error("Failed to save banner:", error);
            notify(error.response?.data?.message || "Failed to save banner", { type: "error" });
        }

        setLoading(false);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString();
    };

    // Target type options
    const targetTypeOptions = [
        { value: "all", label: "All Users" },
        { value: "no_prize_draw_entry", label: "Users without Prize Draw Entry" },
        { value: "no_referrals", label: "Users with No Referrals" },
        { value: "custom", label: "Custom" },
    ];

    // CTA action options
    const ctaActionOptions = [
        { value: "CLOSE", label: "Close Popup" },
        { value: "OPEN_REFERRAL_SHARE", label: "Open Referral Share" },
        { value: "NAVIGATE", label: "Navigate to Screen" },
        { value: "OPEN_URL", label: "Open URL" },
        { value: "NAVIGATE_DASHBOARD", label: "Go to Dashboard Tab" },
        { value: "NAVIGATE_SHOPPING", label: "Go to Shopping Tab" },
        { value: "NAVIGATE_PRIZE", label: "Go to Prize Tab" },
    ];

    if (loading && banners.length === 0) {
        return (
            <div className={classes.main} style={{ justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={classes.main}>
            <Notification />

            <div className={classes.header}>
                <Typography variant="h5" className={classes.title}>
                    Referral Promo Banners
                </Typography>
                {!isEditing && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        Create New Banner
                    </Button>
                )}
            </div>

            {/* Banner List */}
            {!isEditing && (
                <div className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Target</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {banners.map((banner) => (
                                <TableRow key={banner.id}>
                                    <TableCell>{banner.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={banner.is_active ? "Active" : "Inactive"}
                                            size="small"
                                            className={`${classes.statusChip} ${banner.is_active ? classes.activeChip : classes.inactiveChip}`}
                                        />
                                    </TableCell>
                                    <TableCell>{banner.priority}</TableCell>
                                    <TableCell>{banner.target_type}</TableCell>
                                    <TableCell>{formatDate(banner.start_date)}</TableCell>
                                    <TableCell>{formatDate(banner.end_date)}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleEditBanner(banner)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteClick(banner)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {banners.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No banners found. Click "Create New Banner" to add one.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Edit/Create Form */}
            {isEditing && (
                <Form
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    render={({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit}>
                            {/* Section 1: Basic Info */}
                            <Accordion
                                expanded={expandedSections.includes("basic")}
                                onChange={handleAccordionChange("basic")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <SettingsIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Basic Info & Targeting</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="name" component={renderInput} label="Banner Name" placeholder="Prize Draw Promo Jan 2025" />
                                        <Field name="priority" component={renderInput} label="Priority" type="number" placeholder="100" />
                                        <Field name="is_active" component={renderSwitch} label="Active" type="checkbox" />
                                        <Field name="target_type" component={renderSelect} label="Target Audience" options={targetTypeOptions} />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 2: Schedule */}
                            <Accordion
                                expanded={expandedSections.includes("schedule")}
                                onChange={handleAccordionChange("schedule")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <EventIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Schedule</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="start_date" component={renderInput} label="Start Date" type="datetime-local" InputLabelProps={{ shrink: true }} />
                                        <Field name="end_date" component={renderInput} label="End Date" type="datetime-local" InputLabelProps={{ shrink: true }} />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 3: Content */}
                            <Accordion
                                expanded={expandedSections.includes("content")}
                                onChange={handleAccordionChange("content")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <ImageIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Banner Content</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="header_logo_url" component={renderInput} label="Header Logo URL" placeholder="https://cdn.sprive.com/trophy.png" />
                                        <Field name="bg_color_1" component={renderInput} label="Background Color 1" placeholder="#F5A623" />
                                        <Field name="bg_color_2" component={renderInput} label="Background Color 2" placeholder="#D4841C" />
                                        <Field name="title" component={renderInput} label="Title" placeholder="Every week someone earns £1k towards their mortgage!" />
                                        <Field name="subtitle" component={renderInput} label="Subtitle (optional)" placeholder="" />
                                        <Field name="middle_image_url" component={renderInput} label="Middle Image URL" placeholder="https://cdn.sprive.com/winners-showcase.png" />
                                        <Field name="middle_image_width" component={renderInput} label="Image Width (px)" type="number" placeholder="800" />
                                        <Field name="middle_image_height" component={renderInput} label="Image Height (px)" type="number" placeholder="400" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 4: CTAs */}
                            <Accordion
                                expanded={expandedSections.includes("ctas")}
                                onChange={handleAccordionChange("ctas")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <TouchAppIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Call to Action Buttons</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="subtitle2" style={{ marginBottom: 16 }}>Primary CTA</Typography>
                                    <div className={classes.fieldGrid}>
                                        <Field name="cta_primary_text" component={renderInput} label="Button Text" placeholder="Take part" />
                                        <Field name="cta_primary_action" component={renderSelect} label="Action" options={ctaActionOptions} />
                                        {values.cta_primary_action === "NAVIGATE" && (
                                            <Field name="cta_primary_screen_name" component={renderInput} label="Screen Name" placeholder="ReferralScreen" />
                                        )}
                                        {values.cta_primary_action === "OPEN_URL" && (
                                            <Field name="cta_primary_url" component={renderInput} label="URL" placeholder="https://sprive.com" />
                                        )}
                                        <Field name="cta_primary_bg_color" component={renderInput} label="Background Color" placeholder="#1E3A5F" />
                                        <Field name="cta_primary_text_color" component={renderInput} label="Text Color" placeholder="#FFFFFF" />
                                    </div>
                                    <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 16 }}>Secondary CTA (Optional)</Typography>
                                    <div className={classes.fieldGrid}>
                                        <Field name="cta_secondary_text" component={renderInput} label="Button Text" placeholder="Not now" />
                                        <Field name="cta_secondary_action" component={renderSelect} label="Action" options={ctaActionOptions} />
                                        {values.cta_secondary_action === "NAVIGATE" && (
                                            <Field name="cta_secondary_screen_name" component={renderInput} label="Screen Name" placeholder="HomeScreen" />
                                        )}
                                        {values.cta_secondary_action === "OPEN_URL" && (
                                            <Field name="cta_secondary_url" component={renderInput} label="URL" placeholder="https://sprive.com" />
                                        )}
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 6: Footer */}
                            <Accordion
                                expanded={expandedSections.includes("footer")}
                                onChange={handleAccordionChange("footer")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <SettingsIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Footer</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="footer_text" component={renderInput} label="Footer Text" placeholder="All prizes overpay your mortgage." />
                                        <Field name="footer_link_text" component={renderInput} label="Link Text" placeholder="Read terms." />
                                        <Field name="footer_link_url" component={renderInput} label="Link URL" placeholder="https://sprive.com/terms" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Submit/Cancel Buttons */}
                            <div className={classes.submitButton}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                    style={{ marginRight: 16 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : (isCreating ? "Create Banner" : "Save Changes")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelEdit}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Banner</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete "{bannerToDelete?.name}"? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ReferralPromoBannerConfig;
